import {HiristCourseTile} from '@jobseeker/feature-course/components/HiristCourseTile';
import {courseService} from '@jobseeker/common-core/services';
import {AppState, Course} from '@jobseeker/common-core/types';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {
  Badge,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  styled,
  Typography,
  useTheme,
} from '@mui/material';

import {useRouter} from 'next/router';
import {useEffect, useState} from 'react';
import Sticky from 'react-stickynode';
import {
  categoryOptions,
  experienceOptions,
  methodOptions,
} from '../../constants';
import {FilterDialog, SearchBarHirist} from '@jobseeker/lotus/components';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {Image} from '@jobseeker/lotus/components/Image';
import {ChevronDown} from '@jobseeker/lotus/assets/icons';
import {
  getKeyFromPath,
  getQueryParamsAndSeekerId,
  getRefTagAndOrigin,
  handleTracking,
  openUrl,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';
import {useSelector} from 'react-redux';
export const StickyContainer = ({children, page}: any) => {
  const isHirist = process.env.NEXT_PUBLIC_APP_NAME === 'hirist';

  const isJobfeed = true;
  const isLarge = useIsLargeDevice();

  if (isHirist && isLarge) {
    return (
      <Sticky
        enabled={isLarge}
        top={154 + 94 - (isJobfeed ? 34 : 0)}
        innerZ={9}
        enableTransforms={false}
      >
        {children}
      </Sticky>
    );
  } else {
    return <>{children}</>;
  }
};
const renderValue = (selected: any, initial: any) => {
  if (selected == initial) {
    return <Typography fontSize={14}>{selected}</Typography>;
  }

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Box
        sx={{
          width: 4,
          height: 4,
          backgroundColor: 'lightgreen',
          borderRadius: '50%',
        }}
      />
      <Typography fontSize={14}>{selected}</Typography>
    </Box>
  );
};

const StyledFormControl = styled(FormControl)(({theme}) => ({
  backgroundColor: 'white',
  borderRadius: 16, // 4 * 4px = 16px
  minWidth: 150,
}));
const SelectIcon = ({className, open}: any) => {
  const theme = useTheme();
  return (
    <Image
      className={className}
      src={ChevronDown}
      alt={'Drop Down Arrow'}
      width={12}
      height={12}
      style={{
        userSelect: 'none',
        padding: '2px',
        zIndex: open ? 2 : 0,
        marginRight: theme.spacing(1.7),
        marginLeft: theme.spacing(1.7),
        marginTop: theme.spacing(0.4),
        transition: '0.3s ease-in-out',
        opacity: 0.5,
      }}
      objectFit="cover"
    />
  );
};
// Styled component for Select
const StyledSelect = styled(Select)(({theme}) => ({
  borderRadius: 16,
  fontSize: '14px',
  color: 'rgba(0,0,0,0.5)',
  fontWeight: 500,
  '.MuiInputBase-input': {
    paddingTop: '5px',
    paddingBottom: '5px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #7E7E7E33', // Default border color to be always visible
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #7E7E7E33', // Slightly darker on hover
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #7E7E7E33', // Darker when focused
  },
  //   '&.MuiOutlinedInput-root': {
  //     '&:hover': {
  //       border: '0px solid #7E7E7E !important', // Darker when focused
  //     },
}));

// Styled component for MenuItem
const StyledMenuItem = styled(MenuItem)(({theme}) => ({
  fontSize: 16,
  color: '#555555',
  padding: '12px 16px',
  '&:hover': {backgroundColor: '#eff3fa !important'},
  '&.Mui-selected': {
    backgroundColor: '#eff3fa !important',
    '&:hover': {backgroundColor: '#eff3fa !important'},
  },
}));

// Reusable Select Component
const CategorySelect = ({value, onChange, options, renderValue}: any) => {
  return (
    <StyledFormControl variant="outlined" size="small">
      <InputLabel
        shrink={false}
        sx={{fontSize: '14px', color: 'rgba(0,0,0,0.5)'}}
      >
        {value ? '' : 'Category'}
      </InputLabel>
      <StyledSelect
        IconComponent={SelectIcon} // Custom SVG Icon
        value={value}
        onChange={onChange}
        displayEmpty
        renderValue={renderValue} // ✅ plugged in here
        MenuProps={{
          anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
          sx: {
            maxHeight: 400,
            minWidth: '200px !important',
            '& .MuiPaper-root': {
              borderRadius: 1.5,
              border: '1px solid #e9e9e9',
              boxShadow: '0 3px 31px 0 rgba(0,0,0,0.09)',
              minWidth: '220px !important',
            },
            '& .MuiBackdrop-root': {backgroundColor: 'transparent !important'},
          },
          transformOrigin: {vertical: 'top', horizontal: 'left'},
        }}
      >
        {options.map((option: any) => (
          <StyledMenuItem key={option.id} value={option.name}>
            {option.name}
          </StyledMenuItem>
        ))}
      </StyledSelect>
    </StyledFormControl>
  );
};

const HiristCourseFeed = (e: any) => {
  const {
    coursesArr = [],
    showFiltersAndViewAllCoursesText = true,
    title = '',
    handleEnquiryInLoggedOutState = () => {},
    sx = {},
    forUnpublishedCourses = false,
  } = e;

  const [courses, setCourses] = useState<any[]>(coursesArr);
  const isHirist = true;
  const isJobfeed = true;
  const isLarge = useIsLargeDevice();
  const {userId} = useSelector((state: AppState) => state.auth);

  const router = useRouter();
  const {query} = router; // Get URL params
  const [keyword, setKeyword] = useState<string>(String(query.keyword) || '');

  const [category, setCategory] = useState(
    query.category || categoryOptions[0].name
  );
  const [expLevel, setExpLevel] = useState(
    query.experience || experienceOptions[0].name
  );
  const [method, setMethod] = useState(query.method || methodOptions[0].name);

  const [filters, setFilters] = useState({
    method: query.location || methodOptions[0],
    category: query.category || categoryOptions[0],
    experience: query.experience || experienceOptions[0],
  });
  const getDeviceOs = (userAgent: string): string => {
    const isMobile =
      /(iPad|iPhone|Android|webOS|iPod|BlackBerry|IEMobile|Opera Mini|Mobile)/i.test(
        userAgent
      ) || false;

    if (isMobile) {
      if (/android/i.test(userAgent)) {
        return 'android';
      }

      if (/iphone|ipad|ipod|iPhone|ios/.test(userAgent)) {
        return 'ios';
      }
    }

    return 'web';
  };

  // Update state when URL changes (useful for back/forward navigation)
  useEffect(() => {
    setFilters({
      method: query.method || methodOptions[0],
      category: query.category || categoryOptions[0],
      experience: query.experience || experienceOptions[0],
    });
  }, [query]);

  const useUserAgent = () => {
    const [userAgent, setUserAgent] = useState<string>('');

    useEffect(() => {
      if (typeof navigator !== 'undefined') {
        setUserAgent(navigator.userAgent || navigator.vendor || '');
      }
    }, []);

    return userAgent;
  };
  const [clear, setClear] = useState<boolean>(false);

  const theme = useTheme();
  const userAgent = useUserAgent();
  const [loading, setLoading] = useState<boolean>(true);
  const userI = Number(userId);
  const [topOffset, setTopOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setTopOffset(
        scrollY > (query.keyword && query.keyword?.length > 0 ? 0 : 200)
          ? Math.min(
              44,
              scrollY - (query.keyword && query.keyword?.length > 0 ? 0 : 200)
            )
          : 0
      ); // Adjust based on scroll
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (courses.length == 0) {
      setLoading(true);
    }
    const op = async () => {
      try {
        let courses: Course[] = [];
        const resp = await courseService.getCourses({
          userId: Number(userId),
          headers: {
            Origin: process.env.NEXT_PUBLIC_LOAD_CONFIG,
            Referer: process.env.NEXT_PUBLIC_LOAD_CONFIG,
          },
          params: {
            method:
              (methodOptions.find(option => option.name === query.method)?.id ||
                1) - 1,
            catId:
              (categoryOptions.find(option => option.name === query.category)
                ?.id || 1) - 1,
            minExp:
              experienceOptions.find(option => option.name === query.experience)
                ?.minExp || 0,
            maxExp:
              experienceOptions.find(option => option.name === query.experience)
                ?.maxExp || 30,
            keyword: query.keyword || '',
          },
        });
        courses = resp.doc;
        setCourses(courses);
      } catch (err) {
        console.log();
      } finally {
        setLoading(false);
      }
    };
    if (coursesArr?.length === 0) {
      const crs = op();
    }
  }, [filters, userId]);

  const showClear =
    (query.category && query.category !== categoryOptions[0].name) ||
    (query.method && query.method !== methodOptions[0].name) ||
    (query.experience && query.experience !== experienceOptions[0].name);

  return (
    <>
      <Box
        display={{xxs: 'block', md: 'flex'}}
        mb={0}
        mr={query.keyword && query.keyword?.length > 0 ? '32px' : 0}
        justifyContent={
          query.keyword && query.keyword?.length > 0 ? 'space-between' : ''
        }
      >
        <Typography
          component={'h1'}
          sx={{
            fontSize: 20,
            color: '#000',
            fontWeight: 600,
            mb: 2,
            ml: 2.25,
            mt: -1,
          }}
        >
          {query.keyword && query.keyword?.length > 0
            ? `Search for - ${query.keyword}`
            : title?.length > 0
            ? title
            : category == 'Category'
            ? 'Courses'
            : `${category} Courses`}
        </Typography>
        <Box mt={-1} mb={{xxs: 2, md: 0}}>
          {query.keyword && query.keyword?.length > 0 && (
            <SearchBarHirist setKeyword={setKeyword} keyword={keyword} />
          )}
        </Box>
      </Box>
      {Boolean(showFiltersAndViewAllCoursesText) && (
        <StickyContainer>
          <Grid
            container
            sx={{
              px: isHirist ? {xxs: 0, md: 2} : 2,
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              mb: isJobfeed ? {xxs: 2, md: 3} : 3,
              ...(isHirist
                ? {
                    backgroundColor: {md: '#e3e9f0', xxs: 'transparent'},
                    width: {xxs: 'auto', md: '134.4%'},
                    top: isLarge
                      ? `-${104 + (isJobfeed ? 34 - topOffset : 0)}px`
                      : 'unset',
                    py: {xxs: 0, md: 1.5},
                    margin: 'auto',
                    columnGap: 2,
                    justifyContent: 'start',
                    alignItems: 'center',
                    position: !isLarge ? 'fixed' : 'absolute',
                    bottom: isLarge ? 'auto' : '0px',
                    right: isLarge ? 'unset' : '12px',
                    zIndex: isLarge ? 'unset' : 100,
                    mt: isJobfeed ? 0 : 0,
                  }
                : {
                    mt: isJobfeed ? {xxs: 2, md: 3} : 3,
                    columnGap: {xxs: 2, md: 3, lg: 4, xl: 12},
                    justifyContent: 'space-between',
                    alignItems: 'start',
                  }),
              // [theme.breakpoints.down(1200)]: {
              //   flexWrap: 'wrap',
              // },
            }}
            rowGap={{xxs: 1, md: 3}}
            color="gray.dark"
          >
            {isLarge ? (
              <Box
                display="flex"
                gap={2}
                ml={0}
                alignItems="center"
                borderRadius={16} // Increased border radius
                boxShadow={1} // Slight shadow effect
              >
                <span style={{fontSize: 14, fontWeight: 600, color: '#212529'}}>
                  Filter By:
                </span>

                {/* Category Filter */}
                <CategorySelect
                  value={category}
                  onChange={(e: any) => setCategory(e.target.value)}
                  options={categoryOptions}
                  renderValue={(e: any) =>
                    renderValue(e, categoryOptions[0].name)
                  }
                />

                {/* Experience Level Filter */}
                <CategorySelect
                  value={expLevel}
                  onChange={(e: any) => setExpLevel(e.target.value)}
                  options={experienceOptions}
                  renderValue={(e: any) =>
                    renderValue(e, experienceOptions[0].name)
                  }
                />

                {/* Method Filter */}
                <CategorySelect
                  value={method}
                  onChange={(e: any) => setMethod(e.target.value)}
                  options={methodOptions}
                  renderValue={(e: any) =>
                    renderValue(e, methodOptions[0].name)
                  }
                />

                {/* Apply Button (Always Clickable) */}
                <Button
                  onClick={() => {
                    const {refText, origin} = getRefTagAndOrigin();
                    setClear(true);
                    handleTracking('jsClickRefineButton', {
                      event_category: 'CourseListing',
                      Origin: `${origin}`,
                      UserId: `${userI}`,
                      Device: `${getDeviceOs(userAgent)}`,
                      Ref: `${refText}`,
                      Type: `course`,
                      Exp: `${expLevel}`,
                      Method: `${method}`,
                    });

                    TrackEventUBA('filterCourseListingClick', 'click', {
                      ref: getKeyFromPath('ref') || '',
                      page: 'CourseListing',
                      filterCategory: category || '',
                      filterExp: expLevel || '',
                      filterMethod: method || '',
                      sessionUsername: sessionStorage.getItem('userId') || '',
                    });

                    router.push(
                      {
                        pathname: router.pathname,
                        query: {
                          category: category, // Use the selected category ID
                          method: method,
                          experience: expLevel,
                          keyword:
                            keyword && keyword != 'undefined' ? keyword : '',
                        },
                      },
                      undefined,
                      {shallow: true} // Prevents full reload
                    );
                  }}
                  sx={{
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: '16px',
                    padding: '2px 24px',
                    display: 'flex',
                    zIndex: 1200,
                    alignItems: 'center',
                    transition: 'background-color 0.3s ease-in-out',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main, // Prevents hover background change
                      border: `1px solid ${theme.palette.primary.main}`, // Keeps the same border
                      color: '#fff !important',
                    },
                  }}
                >
                  <Typography
                    className="text"
                    sx={{
                      fontFamily: 'Nunito Sans',
                      fontSize: 14,
                      fontWeight: 500,
                      transition: 'color 0.3s ease-in-out',
                      '&:hover': {
                        color: '#fff !important',
                      },
                    }}
                  >
                    Apply
                  </Typography>
                </Button>
                {showClear && (
                  <Typography
                    onClick={() => {
                      const keyword = router.query.keyword;
                      const query = keyword ? {keyword} : {};

                      router.push(
                        {
                          pathname: router.pathname,
                          query,
                        },
                        undefined,
                        {shallow: true}
                      );
                      setCategory(categoryOptions[0].name);
                      setExpLevel(experienceOptions[0].name);

                      setMethod(methodOptions[0].name);
                    }}
                    fontSize={14}
                    color={theme.palette.primary.main}
                    sx={{cursor: 'pointer', whiteSpace: 'nowrap'}}
                  >
                    Clear All
                  </Typography>
                )}
              </Box>
            ) : (
              <>
                <Badge
                  overlap="circular"
                  color={'success'}
                  badgeContent=" "
                  invisible={!showClear}
                  sx={{
                    margin: theme.spacing(0.875),
                    '& .MuiBadge-badge': {
                      color: theme.palette.error.dark,
                      width: theme.spacing(1),
                      height: theme.spacing(1),
                      padding: 0,
                      minWidth: theme.spacing(1),
                      top: '20%',
                      right: '28%',
                    },
                  }}
                >
                  <FilterDialog
                    variant={VersionVariants.VERSION_TWO}
                    onFilter={() => {
                      const {refText, origin} = getRefTagAndOrigin();

                      handleTracking('jsClickRefineButton', {
                        event_category: 'JobListing',
                        Origin: `${origin}`,
                        UserId: `${userI}`,
                        Device: `${getDeviceOs(userAgent)}`,
                        Ref: `${refText}`,
                        Type: `course`,
                        Exp: `${expLevel}`,
                        Method: `${method}`,
                      });
                      router.push(
                        {
                          pathname: router.pathname,
                          query: {
                            category: category, // Use the selected category ID
                            method: method,
                            experience: expLevel,
                            keyword:
                              keyword && keyword != 'undefined' ? keyword : '',
                          },
                        },
                        undefined,
                        {shallow: true} // Prevents full reload
                      );
                    }}
                    isFilter={true}
                    onChange={(data: any) => {
                      console.log({data});
                      if (data.category) {
                        setCategory(
                          categoryOptions.find(
                            option => option.id === data.category
                          )?.name || 'Category'
                        );
                      }
                      if (data.experience) {
                        setExpLevel(
                          experienceOptions.find(
                            option => option.id === data.experience
                          )?.name || 'Any Exp. Level'
                        );
                      }
                      if (data.method) {
                        setMethod(
                          methodOptions.find(
                            option => option.id === data.method
                          )?.name || 'Method'
                        );
                      }
                    }}
                    formValue={{
                      category: categoryOptions.find(
                        option => option.name === category
                      )?.id,
                      experience: experienceOptions.find(
                        option => option.name === expLevel
                      )?.id,
                      method: methodOptions.find(
                        option => option.name === method
                      )?.id,
                    }}
                    showCategory={false}
                    showSortBy={false}
                    isLoading={false}
                    industryOptions={[]}
                    courses={true}
                  />
                </Badge>
              </>
            )}
          </Grid>
        </StickyContainer>
      )}
      <Box sx={{ml: 1.25}}>
        {loading && courses.length == 0 ? (
          <>
            <h1></h1>
            {[...new Array(15)].map(index => {
              return (
                <Box display="flex" key={`jobfeed-skel-${index}`}>
                  <Skeleton
                    variant="rounded"
                    height={60}
                    sx={{
                      width: {xxs: '75px', md: '65px'},
                      mt: {xxs: 0, md: 1},
                      ml: 2,
                      mb: 1,
                    }}
                  />
                  <Box display="block" width="100%">
                    <Skeleton
                      variant="rounded"
                      width={'50%'}
                      height={20}
                      sx={{
                        mt: {xxs: 0, md: 1},
                        ml: {xxs: 2, md: 'none'},
                        mb: 1,
                      }}
                    />
                    <Skeleton
                      variant="rounded"
                      width={'90%'}
                      height={20}
                      sx={{
                        mt: {xxs: 0, md: 1},
                        ml: {xxs: 2, md: 'none'},
                        mb: 1,
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </>
        ) : (
          courses?.map((e: any, idx) => (
            <HiristCourseTile
              key={idx}
              data={e}
              handleEnquiryInLoggedOutState={handleEnquiryInLoggedOutState}
              sx={sx}
              forUnpublishedCourses={forUnpublishedCourses}
            />
          ))
        )}

        {courses.length > 0 && Boolean(showFiltersAndViewAllCoursesText) && (
          <Button
            onClick={() => {
              const {origin, refText} = getRefTagAndOrigin();
              const device = getDeviceOs(userAgent);
              handleTracking(
                'lgClickViewAllCourses',

                {
                  event_category: 'LearnAndGrow',
                  Origin: `${origin}`,
                  Source: 'Navigation',
                  type: 'secondary',
                  name: `${name}`,
                  UserId: `${userI}`,
                  Status: `${
                    userI == 0 || userI == -1 ? 'LoggedOut' : 'LoggedIn'
                  }`,
                  Device: `${device}`,
                  Ref: `${refText}`,
                }
              );
              //uba event for view all courses
              const {queryParams} = getQueryParamsAndSeekerId();
              TrackEventUBA('viewAllCoursesClick', 'click', {
                ref: queryParams.ref || '',
                page: 'CourseListing',
                sessionUsername: userI ? userI.toString() : '',
              });
              openUrl('/learning?ref=nav', '_blank');
            }}
            sx={{
              width: {xxs: 'calc(100% - 76px)', md: 180},
              border: `1.5px solid ${theme.palette.primary.main}`,
              fontWeight: 600,
              borderRadius: '3px',
              mx: {xxs: 5, md: '433px'},
              mt: {xxs: 0, md: 4.75},
            }}
          >
            View All Courses
          </Button>
        )}
        {!loading && courses.length == 0 && (
          <>
            <h1 style={{fontSize: 24, fontWeight: 600, textAlign: 'center'}}>
              {'Sorry, No result found :('}
              <br></br>
              To get results, Please change your search criteria or filters like
              posting range from the above.
            </h1>
            <Image
              src="https://job-static.hirist.com/V8/static/media/no-jobfeed.7771e408.svg"
              alt="NoNotifications"
              width={350}
              height={400}
              style={{
                objectFit: 'contain',
                width: '70%',
                height: '70%',
                marginLeft: '15%',
              }}
            />
          </>
        )}
      </Box>
    </>
  );
};
export default HiristCourseFeed;
